import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';

const WHATSAPP_NUMBER = '221774884254';

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const getServiceLabel = (value) => {
    const services = {
      'simple-transfer': 'Transfert simple',
      'airport-transfer': 'Transfert aéroport',
      'hourly-service': 'Mise à disposition',
      'event-transport': 'Transport événementiel'
    };
    return services[value] || value;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    let message = `━━━━━━━━━━━━━━━━━━━━━\n`;
    message += `    🚘 *BARHAM DRIVER*\n`;
    message += `    _Demande de Devis_\n`;
    message += `━━━━━━━━━━━━━━━━━━━━━\n\n`;

    message += `📋 *Service demandé:* ${getServiceLabel(formData.service)}\n\n`;

    message += `┌─────────────────────\n`;
    message += `│ 👤 *INFORMATIONS CLIENT*\n`;
    message += `├─────────────────────\n`;
    message += `│ Nom: ${formData.firstName} ${formData.lastName}\n`;
    if (formData.company) message += `│ Entreprise: ${formData.company}\n`;
    message += `│ Tél: ${formData.phone}\n`;
    message += `│ Email: ${formData.email}\n`;
    message += `└─────────────────────\n\n`;

    if (formData.message) {
      message += `┌─────────────────────\n`;
      message += `│ 💬 *MESSAGE*\n`;
      message += `├─────────────────────\n`;
      message += `│ ${formData.message}\n`;
      message += `└─────────────────────\n`;
    }

    message += `\n━━━━━━━━━━━━━━━━━━━━━\n`;
    message += `   _Merci de votre confiance_`;

    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');

    setFormData({
      firstName: '', lastName: '', email: '', phone: '',
      company: '', service: '', message: ''
    });
    setIsSubmitting(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 bg-[#0f0f10]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 text-center">
          <h1
            className="text-3xl sm:text-4xl md:text-5xl text-white mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Contactez-nous
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto text-sm sm:text-base">
            Prenez contact avec notre équipe pour un devis personnalisé ou toute question sur nos services.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 sm:py-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Contact Info */}
            <div>
              <h2 
                className="text-2xl text-gray-900 mb-8"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Nous contacter
              </h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Phone size={20} className="text-gray-700" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Téléphone</h3>
                    <p className="text-gray-600">+221 77 488 42 54</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Mail size={20} className="text-gray-700" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-600">Sandierygassama8@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <MapPin size={20} className="text-gray-700" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Adresse</h3>
                    <p className="text-gray-600">Rufisque cité Senelec villa 237, Sénégal</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Clock size={20} className="text-gray-700" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Disponibilité</h3>
                    <p className="text-gray-600">24/7 - Partout au Sénégal</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">Devis sous 24h</h3>
                <p className="text-gray-600 text-sm">
                  Notre équipe s'engage à répondre à toutes les demandes de devis sous 24 heures. 
                  Pour les demandes urgentes, veuillez nous appeler directement.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-gray-50 p-4 sm:p-8 rounded-lg">
                <h2 
                  className="text-2xl text-gray-900 mb-6"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Demander un devis
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                      placeholder="Prénom"
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      className="h-12"
                      required
                    />
                    <Input
                      placeholder="Nom"
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      className="h-12"
                      required
                    />
                  </div>
                  <Input
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="h-12"
                    required
                  />
                  <Input
                    type="tel"
                    placeholder="Numéro de téléphone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="h-12"
                    required
                  />
                  <Input
                    placeholder="Entreprise (optionnel)"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    className="h-12"
                  />
                  <Select
                    value={formData.service}
                    onValueChange={(value) => handleChange('service', value)}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Sélectionnez un service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="simple-transfer">Transfert simple</SelectItem>
                      <SelectItem value="airport-transfer">Transfert aéroport</SelectItem>
                      <SelectItem value="hourly-service">Mise à disposition</SelectItem>
                      <SelectItem value="event-transport">Transport événementiel</SelectItem>
                    </SelectContent>
                  </Select>
                  <textarea
                    placeholder="Votre message"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="w-full h-32 p-3 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900/20 resize-none"
                    required
                  />
                  <Button
                    type="submit"
                    className="w-full h-12 bg-gray-900 hover:bg-gray-800 text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
